/**
 * Core-100 R3 — the shop's alert thresholds (owner decisions OD-18 / OD-19 keep the defaults "pending ruling";
 * the shop can already set its own):
 *  · how long a kicked ticket may wait for acceptance before people are told (CORE-AK, R2-40) —
 *    `set_custody_escalation_thresholds`;
 *  · after how many hours a running timer is brought to a person (CORE-AJ, R2-69) — `set_abandoned_timer_hours`.
 * Both are notices only: an escalation never moves custody, and a timer is never stopped for anyone.
 * Managers / owners change them; the database enforces that and the bounds.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Settings { enabled: boolean; front_office_accept_minutes: number; back_office_accept_minutes: number; abandoned_timer_hours: number }
const DEFAULTS: Settings = { enabled: true, front_office_accept_minutes: 15, back_office_accept_minutes: 15, abandoned_timer_hours: 12 };
// A member call, never a detached `supabase.rpc` (a detached method loses its client and throws before any request).
type LooseRpc = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> };
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as unknown as LooseRpc).rpc(fn, args);

export function validateAlertThresholds(s: Settings): string | null {
  for (const [label, v] of [['Front-office wait', s.front_office_accept_minutes], ['Technician wait', s.back_office_accept_minutes]] as const) {
    if (!Number.isInteger(v) || v < 1 || v > 480) return `${label} must be a whole number of minutes between 1 and 480.`;
  }
  if (!Number.isInteger(s.abandoned_timer_hours) || s.abandoned_timer_hours < 2 || s.abandoned_timer_hours > 72) {
    return 'The open-timer check must be a whole number of hours between 2 and 72.';
  }
  return null;
}

export function AlertThresholdsCard({ shopId, canEdit }: { shopId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['alert-thresholds', shopId],
    queryFn: async (): Promise<Settings> => {
      const { data, error } = await supabase.from('custody_escalation_settings' as never)
        .select('enabled, front_office_accept_minutes, back_office_accept_minutes, abandoned_timer_hours').eq('shop_id', shopId).maybeSingle();
      if (error) throw error;
      return (data as Settings | null) ?? DEFAULTS;
    },
  });
  const [form, setForm] = useState<Settings>(DEFAULTS);
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  const save = async () => {
    const problem = validateAlertThresholds(form);
    if (problem) { toast({ title: 'Not saved', description: problem, variant: 'destructive' }); return; }
    // One write for all four values (set_alert_thresholds): a refusal saves nothing — never a partial save.
    const { error } = await rpc('set_alert_thresholds', {
      p_shop_id: shopId, p_enabled: form.enabled,
      p_front_office_minutes: form.front_office_accept_minutes, p_back_office_minutes: form.back_office_accept_minutes,
      p_abandoned_timer_hours: form.abandoned_timer_hours,
    });
    if (error) {
      toast({ title: 'Not saved', description: /MANAGER_ONLY/.test(error.message) ? 'Only a manager or owner changes these.' : 'The thresholds were not saved.', variant: 'destructive' });
      return;
    }
    toast({ title: 'Saved', description: 'Alert thresholds updated.' });
    void qc.invalidateQueries({ queryKey: ['alert-thresholds', shopId] });
  };

  const num = (k: keyof Settings) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));
  return (
    <Card data-testid="alert-thresholds-card">
      <CardHeader>
        <CardTitle>Alerts</CardTitle>
        <CardDescription>
          When to tell people something is waiting. These are notices only — nothing is moved, accepted or stopped for anyone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {q.isError && <p className="text-destructive">The current thresholds could not be loaded.</p>}
        {q.isLoading && <p className="text-muted-foreground">Loading…</p>}
        {/* The form appears only once the shop's current values are known, so a load can never overwrite input. */}
        {q.isSuccess && (<>
        <label className="flex items-center justify-between gap-4">
          <span>Tell people when a handed-over ticket is not accepted</span>
          <Switch checked={form.enabled} disabled={!canEdit} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} aria-label="Escalate unaccepted tickets" />
        </label>
        <label className="flex items-center justify-between gap-4">
          <span>…after the front office has not accepted it for (minutes)</span>
          <Input className="w-24" type="number" inputMode="numeric" disabled={!canEdit} value={form.front_office_accept_minutes} onChange={num('front_office_accept_minutes')} aria-label="Front-office wait minutes" />
        </label>
        <label className="flex items-center justify-between gap-4">
          <span>…after a technician has not accepted it for (minutes)</span>
          <Input className="w-24" type="number" inputMode="numeric" disabled={!canEdit} value={form.back_office_accept_minutes} onChange={num('back_office_accept_minutes')} aria-label="Technician wait minutes" />
        </label>
        <label className="flex items-center justify-between gap-4">
          <span>Bring a running timer to a person after (hours)</span>
          <Input className="w-24" type="number" inputMode="numeric" disabled={!canEdit} value={form.abandoned_timer_hours} onChange={num('abandoned_timer_hours')} aria-label="Open timer hours" />
        </label>
        {canEdit && <Button onClick={() => void save()}>Save alerts</Button>}
        </>)}
      </CardContent>
    </Card>
  );
}
